import { getHorizonUrl } from './stellar';

interface AssetInfo {
  code: string;
  issuer?: string;
  image?: string;
  name?: string;
}

interface SEP1TomlAsset {
  code: string;
  issuer: string;
  image?: string;
  desc?: string;
  name?: string;
}

// Generate deterministic color for asset based on code and issuer
export const getAssetColor = (assetCode: string, assetIssuer?: string): { hue: number; saturation: number; lightness: number } => {
  const input = `${assetCode}${assetIssuer || ''}`;
  let hash = 0;

  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) - hash) + input.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Generate visually pleasing colors
  const hue = Math.abs(hash % 360);
  const saturation = 65 + (Math.abs(hash >> 8) % 20); // 65-85%
  const lightness = 50 + (Math.abs(hash >> 16) % 15); // 50-65%

  return { hue, saturation, lightness };
};

// Enhanced caching system with TOML-level coherence
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

// Cache for SEP1 TOML data per domain
const tomlCache = new Map<string, CacheEntry<SEP1TomlAsset[]>>();

// Cache for asset info
const assetInfoCache = new Map<string, CacheEntry<AssetInfo>>();

// Cache expiry times
const TOML_CACHE_DURATION = 24 * 60 * 60 * 1000; // 1 day
const ASSET_INFO_CACHE_DURATION = 24 * 60 * 60 * 1000; // 1 day
const ASSET_INFO_SHORT_CACHE = 30 * 60 * 1000; // 30 minutes for failed lookups
const STORAGE_KEY_PREFIX = 'stellar_asset_cache_v4_'; // Bumped to v4 for cache coherence
const TOML_STORAGE_PREFIX = 'stellar_toml_cache_v4_';

// Load caches from localStorage on startup
const loadCacheFromStorage = () => {
  try {
    // Load asset cache
    const assetKeys = Object.keys(localStorage).filter(key => key.startsWith(STORAGE_KEY_PREFIX));
    assetKeys.forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        const parsed = JSON.parse(data);
        const assetKey = key.replace(STORAGE_KEY_PREFIX, '');
        
        if (parsed.expiresAt > Date.now()) {
          assetInfoCache.set(assetKey, parsed);
        } else {
          localStorage.removeItem(key);
        }
      }
    });
    
    // Load TOML cache
    const tomlKeys = Object.keys(localStorage).filter(key => key.startsWith(TOML_STORAGE_PREFIX));
    tomlKeys.forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        const parsed = JSON.parse(data);
        const tomlKey = key.replace(TOML_STORAGE_PREFIX, '');
        
        if (parsed.expiresAt > Date.now()) {
          tomlCache.set(tomlKey, parsed);
        } else {
          localStorage.removeItem(key);
        }
      }
    });
  } catch {
    // Ignore storage errors
  }
};

// Save cache entries to localStorage
const saveCacheToStorage = (key: string, entry: CacheEntry<AssetInfo>) => {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + key, JSON.stringify(entry));
  } catch {
    // Ignore storage errors
  }
};

const saveTomlToStorage = (key: string, entry: CacheEntry<SEP1TomlAsset[]>) => {
  try {
    localStorage.setItem(TOML_STORAGE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // Ignore storage errors
  }
};

// Initialize cache from storage
loadCacheFromStorage();

/** Letters, digits and hyphens in dot-separated labels, ending with an alphabetic TLD. */
const HOSTNAME = /^(?=.{3,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

// Helper function to fetch and cache TOML data for a domain
const fetchTomlForDomain = async (homeDomain: string, network: 'mainnet' | 'testnet'): Promise<SEP1TomlAsset[]> => {
  const tomlCacheKey = `${homeDomain}:${network}`;
  
  // Check TOML cache first
  const cachedToml = tomlCache.get(tomlCacheKey);
  if (cachedToml && cachedToml.expiresAt > Date.now()) {
    return cachedToml.data;
  }
  
  // Only a bare public hostname: no IP address, port, path, query or credentials.
  if (!HOSTNAME.test(homeDomain) || /^[\d.]+$/.test(homeDomain)) {
    // Cache empty result for invalid domains
    const now = Date.now();
    const emptyEntry: CacheEntry<SEP1TomlAsset[]> = {
      data: [],
      timestamp: now,
      expiresAt: now + TOML_CACHE_DURATION
    };
    tomlCache.set(tomlCacheKey, emptyEntry);
    saveTomlToStorage(tomlCacheKey, emptyEntry);
    return [];
  }
  
  try {
    const tomlUrl = `https://${homeDomain}/.well-known/stellar.toml`;
    const tomlResponse = await fetch(tomlUrl, { 
      mode: 'cors',
      signal: AbortSignal.timeout(5000) 
    });
    
    if (!tomlResponse.ok) {
      throw new Error(`Non-2xx response: ${tomlResponse.status}`);
    }
    
    const tomlContent = await tomlResponse.text();
    
    if (!tomlContent) {
      throw new Error('Empty TOML response');
    }
    
    // Parse TOML currencies
    const currencies = parseTomlCurrencies(tomlContent);
    
    // Cache the TOML data
    const now = Date.now();
    const tomlCacheEntry: CacheEntry<SEP1TomlAsset[]> = {
      data: currencies,
      timestamp: now,
      expiresAt: now + TOML_CACHE_DURATION
    };
    tomlCache.set(tomlCacheKey, tomlCacheEntry);
    saveTomlToStorage(tomlCacheKey, tomlCacheEntry);
    
    return currencies;
  } catch (error) {
    // Cache empty result for 24h to avoid repeated attempts
    const now = Date.now();
    const emptyEntry: CacheEntry<SEP1TomlAsset[]> = {
      data: [],
      timestamp: now,
      expiresAt: now + TOML_CACHE_DURATION
    };
    tomlCache.set(tomlCacheKey, emptyEntry);
    saveTomlToStorage(tomlCacheKey, emptyEntry);
    return [];
  }
};

// Helper to resolve image URLs properly
const resolveImageUrl = (image: string, homeDomain: string): string => {
  // Handle ipfs:// protocol
  if (image.startsWith('ipfs://')) {
    const cid = image.replace('ipfs://', '');
    return `https://ipfs.io/ipfs/${cid}`;
  }
  
  // Force HTTPS for http:// URLs
  if (image.startsWith('http://')) {
    return image.replace('http://', 'https://');
  }
  
  // Already HTTPS
  if (image.startsWith('https://')) {
    return image;
  }
  
  // Relative URL - resolve against home domain
  try {
    const baseUrl = `https://${homeDomain}/`;
    return new URL(image, baseUrl).toString();
  } catch {
    return image;
  }
};

// Curated icons for assets whose issuers publish no usable SEP-1 image.
// Bundled locally so they never depend on an issuer CDN, IPFS gateway or CORS policy.
const ASSET_FALLBACKS: Record<string, AssetInfo> = {
  // EURC from Circle
  'EURC:GDHU6WRG4IEQXM5NZ4BMPKOXHW76MZM4Y2IEMFDVXBSDP6SJY4ITNPP2': {
    code: 'EURC',
    issuer: 'GDHU6WRG4IEQXM5NZ4BMPKOXHW76MZM4Y2IEMFDVXBSDP6SJY4ITNPP2',
    name: 'Euro Coin',
    image: `${import.meta.env.BASE_URL}images/assets/eurc.png`
  },
  // USDC from Circle
  'USDC:GA5ZSEJYB37JRC5AVCIA5MOP4RHTM335X2KGX3IHOJAPP5RE34K4KZVN': {
    code: 'USDC',
    issuer: 'GA5ZSEJYB37JRC5AVCIA5MOP4RHTM335X2KGX3IHOJAPP5RE34K4KZVN',
    name: 'USD Coin',
    image: `${import.meta.env.BASE_URL}images/assets/usdc.png`
  },
  // BLND from Blend
  'BLND:GDJEHTBE6ZHUXSWFI642DCGLUOECLHPF3KSXHPXTSTJ7E3JF6MQ5EZYY': {
    code: 'BLND',
    issuer: 'GDJEHTBE6ZHUXSWFI642DCGLUOECLHPF3KSXHPXTSTJ7E3JF6MQ5EZYY',
    name: 'Blend',
    image: `${import.meta.env.BASE_URL}images/assets/blnd.png`
  },
  // Native XLM
  'XLM:': {
    code: 'XLM',
    name: 'Stellar Lumens',
    image: `${import.meta.env.BASE_URL}xlm-logo.png`
  }
};


// In-flight request deduplication to prevent multiple simultaneous requests for the same asset
const pendingRequests = new Map<string, Promise<AssetInfo>>();

export const fetchAssetInfo = async (assetCode: string, assetIssuer?: string, network: 'mainnet' | 'testnet' = 'mainnet'): Promise<AssetInfo> => {
  // Check for well-known asset fallbacks first
  const fallbackKey = `${assetCode}:${assetIssuer || ''}`;
  if (ASSET_FALLBACKS[fallbackKey]) {
    return ASSET_FALLBACKS[fallbackKey];
  }
  
  // Native XLM handling
  if ((!assetCode || assetCode === 'XLM') && !assetIssuer) {
    return ASSET_FALLBACKS['XLM:'];
  }

  // Create a simple, stable cache key (no timestamps that change every millisecond!)
  const assetCacheKey = `${assetCode}:${assetIssuer}:${network}`;
  
  // Check cache FIRST before any network requests
  const cachedAssetInfo = assetInfoCache.get(assetCacheKey);
  if (cachedAssetInfo && cachedAssetInfo.expiresAt > Date.now()) {
    return cachedAssetInfo.data;
  }

  // Check if there's already a pending request for this asset to prevent duplicates
  if (pendingRequests.has(assetCacheKey)) {
    return pendingRequests.get(assetCacheKey)!;
  }

  // Create the promise for this asset request
  const assetPromise = fetchAssetInfoInternal(assetCode, assetIssuer, network, assetCacheKey);
  pendingRequests.set(assetCacheKey, assetPromise);

  try {
    const result = await assetPromise;
    return result;
  } finally {
    // Always clean up the pending request
    pendingRequests.delete(assetCacheKey);
  }
};

// Internal function that does the actual fetching
const fetchAssetInfoInternal = async (assetCode: string, assetIssuer: string | undefined, network: 'mainnet' | 'testnet', cacheKey: string): Promise<AssetInfo> => {
  try {
    // Fetch issuer's home domain from Horizon
    const horizonUrl = getHorizonUrl(network);
    const accountUrl = `${horizonUrl}/accounts/${assetIssuer}`;
    const accountResponse = await fetch(accountUrl);
    
    if (!accountResponse.ok) throw new Error('Account fetch failed');
    
    const accountData = await accountResponse.json();
    const homeDomain = accountData.home_domain;
    
    if (!homeDomain) throw new Error('No home domain');
    
    // Fetch TOML data (uses cache if available)
    const currencies = await fetchTomlForDomain(homeDomain, network);
    
    // Find matching asset in TOML data
    const matchingAsset = currencies.find(currency => 
      currency.code.toLowerCase() === assetCode.toLowerCase() && 
      currency.issuer === assetIssuer
    );
    
    let resolvedImage: string | undefined;
    if (matchingAsset?.image) {
      resolvedImage = resolveImageUrl(matchingAsset.image, homeDomain);
    }
    
    const assetInfo: AssetInfo = {
      code: assetCode,
      issuer: assetIssuer,
      name: matchingAsset?.name || matchingAsset?.desc || assetCode,
      image: resolvedImage
    };
    
    // Cache with appropriate expiry
    const now = Date.now();
    const ttl = resolvedImage ? ASSET_INFO_CACHE_DURATION : ASSET_INFO_SHORT_CACHE;
    const assetCacheEntry: CacheEntry<AssetInfo> = {
      data: assetInfo,
      timestamp: now,
      expiresAt: now + ttl
    };
    assetInfoCache.set(cacheKey, assetCacheEntry);
    saveCacheToStorage(cacheKey, assetCacheEntry);
    
    return assetInfo;
  } catch (error) {
    // Fallback: return basic info without image
    const assetInfo: AssetInfo = {
      code: assetCode,
      issuer: assetIssuer,
      name: assetCode
    };
    
    // Cache failure for shorter duration
    const now = Date.now();
    const assetCacheEntry: CacheEntry<AssetInfo> = {
      data: assetInfo,
      timestamp: now,
      expiresAt: now + ASSET_INFO_SHORT_CACHE
    };
    assetInfoCache.set(cacheKey, assetCacheEntry);
    saveCacheToStorage(cacheKey, assetCacheEntry);
    
    return assetInfo;
  }
};

// Simple TOML parser for CURRENCIES section
function parseTomlCurrencies(toml: string): SEP1TomlAsset[] {
  const assets: SEP1TomlAsset[] = [];
  // Normalize line endings and split
  const lines = toml.replace(/\r\n/g, '\n').split('\n');
  let inCurrenciesSection = false;
  let currentAsset: Partial<SEP1TomlAsset> = {};
  
  for (let line of lines) {
    // Strip inline comments
    const commentIndex = line.indexOf('#');
    if (commentIndex !== -1) {
      line = line.substring(0, commentIndex);
    }
    const trimmed = line.trim();
    
    if (trimmed === '[[CURRENCIES]]') {
      if (Object.keys(currentAsset).length > 0 && currentAsset.code) {
        assets.push(currentAsset as SEP1TomlAsset);
      }
      currentAsset = {};
      inCurrenciesSection = true;
      continue;
    }
    
    if (trimmed.startsWith('[') && trimmed !== '[[CURRENCIES]]') {
      inCurrenciesSection = false;
      if (Object.keys(currentAsset).length > 0 && currentAsset.code) {
        assets.push(currentAsset as SEP1TomlAsset);
      }
      currentAsset = {};
      continue;
    }
    
    if (inCurrenciesSection && trimmed.includes('=')) {
      const [key, ...valueParts] = trimmed.split('=');
      let value = valueParts.join('=').trim();
      // Remove surrounding quotes
      value = value.replace(/^["']|["']$/g, '');
      
      switch (key.trim().toLowerCase()) {
        case 'code':
          currentAsset.code = value;
          break;
        case 'issuer':
          currentAsset.issuer = value;
          break;
        case 'image':
          currentAsset.image = value;
          break;
        case 'desc':
          currentAsset.desc = value;
          break;
        case 'name':
          currentAsset.name = value;
          break;
      }
    }
  }
  
  // Don't forget the last asset
  if (Object.keys(currentAsset).length > 0 && currentAsset.code) {
    assets.push(currentAsset as SEP1TomlAsset);
  }
  
  return assets;
}
